// /mfa-legalisation/$country — TH per-country detail (Phase P-MFA-LEGALISATION)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import CountryDeepContent from "@/components/CountryDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MFA_COUNTRIES, MFA_COUNTRY_SLUGS, getMfaCountry } from "@/data/mfa-legalisation-countries";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/mfa-legalisation/$country")({
  beforeLoad: ({ params }) => {
    if (!MFA_COUNTRY_SLUGS.includes(params.country)) throw notFound();
  },
  head: ({ params }) => {
    const c = getMfaCountry(params.country)!;
    const thPath = `/mfa-legalisation/${c.slug}`;
    const enPath = `/en/mfa-legalisation/${c.slug}`;
    const title = `รับรองเอกสารสถานทูต${c.nameTh} + MFA — ${c.totalDays} วัน | NYC`;
    const desc = `${c.leadTh} · แปล: ${c.translationLang} · ค่าสถานทูต · จบ ${c.totalDays} วัน. ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    const steps = [
      { name: "แปลและรับรองนักแปลขึ้นทะเบียน", text: `แปลเป็น ${c.translationLang} โดยนักแปลขึ้นทะเบียนสถานทูต${c.nameTh}` },
      { name: "Notary Public", text: "Notarial Services Attorney รับรองลายมือชื่อและสำเนาถูกต้อง ภายในวัน" },
      { name: "MFA แจ้งวัฒนะ", text: `กรมการกงสุล กระทรวงการต่างประเทศ — ปกติ 2 วันทำการ ค่า 200 บาท/ฉบับ ด่วน 1 วัน 400 บาท` },
      { name: `สถานทูต${c.nameTh}`, text: `${c.embassyAddress} — ${c.embassyPhone}. ค่ารับรอง ${c.embassyFeeTHB.toLocaleString()} บาท/ฉบับ` },
      { name: "ส่งคืนถึงบ้าน/ออฟฟิศ", text: "Courier ทั่วกรุงเทพฯ ฟรีเมื่อยอด > 5,000 บาท · EMS ทั่วประเทศ" },
    ];
    const faqs = [
      { q: `เอกสารใดบ้างที่ส่ง${c.nameTh}ได้?`, a: `เอกสารยอดนิยม: ${c.topDocs.join(", ")} — NYC รับเอกสารอื่นได้ตามคำขอ` },
      { q: `ต้องแปลเป็นภาษาอะไร?`, a: c.translationLang },
      { q: `ใช้เวลาเท่าไหร่?`, a: `${c.totalDays} วันทำการ end-to-end ถ้าเอกสารพร้อม — ด่วนพิเศษติดต่อ LINE @nyc-notary` },
      { q: `ราคารวมทั้งหมดเท่าไหร่?`, a: `เริ่ม 3,000 บาท/ฉบับ (รวม MFA + courier) + ค่าสถานทูต${c.nameTh} ${c.embassyFeeTHB.toLocaleString()} บาท + แปลตามจำนวนหน้า (เริ่ม 800/หน้า)` },
      { q: `${c.nameTh}อยู่ใน Hague Apostille หรือไม่?`, a: c.noteTh },
      { q: `สถานทูต${c.nameTh}อยู่ที่ไหน?`, a: `${c.embassyAddress} โทร ${c.embassyPhone}` },
    ];
    const breadcrumbs = [
      { name: "หน้าแรก", path: "/" },
      { name: "MFA Legalisation", path: "/mfa-legalisation" },
      { name: c.nameTh, path: thPath },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/mfa-legalisation/$country", "th") }, { name: "description", content: ctaDesc(desc, "/mfa-legalisation/$country", "th")},
        { name: "keywords", content: c.keywordsTh.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/mfa-legalisation/$country", "th")}, { property: "og:description", content: ctaDesc(desc, "/mfa-legalisation/$country", "th")},
        { property: "og:url", content: `${SITE}${thPath}` }, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: buildLandingSchemas({
        path: thPath, title, description: desc,
        serviceType: `MFA + Embassy Legalisation for ${c.countryEn}`,
        priceFromThb: 3000, steps, faqs, breadcrumbs,
      }),
    };
  },
  component: MfaDetailTh,
});

function MfaDetailTh() {
  const { country } = Route.useParams();
  const c = getMfaCountry(country)!;
  const nearby = MFA_COUNTRIES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 6);
  return (
    <>
      <ServiceLanding
        locale="th"
        badge={`${c.flag} ${c.nameTh} · Non-Apostille`}
        title={`รับรองเอกสารสำหรับ${c.nameTh} — MFA + สถานทูต`}
        subtitle={c.leadTh}
        quickAnswer={{
          q: `เอกสารส่ง${c.nameTh}ต้องผ่านขั้นตอนอะไรบ้าง?`,
          a: `แปล → Notary → MFA แจ้งวัฒนะ → สถานทูต${c.nameTh} (${c.embassyAddress.split(",")[0]}) — จบ ${c.totalDays} วัน เริ่ม 3,000 บาท/ฉบับ + ค่าสถานทูต ${c.embassyFeeTHB.toLocaleString()} บาท. ${c.noteTh}`,
        }}
        keyFacts={[
          { k: "ภูมิภาค", v: c.region },
          { k: "ภาษาแปล", v: c.translationLang },
          { k: "ระยะเวลา", v: `${c.totalDays} วันทำการ` },
          { k: "ค่าสถานทูต", v: `${c.embassyFeeTHB.toLocaleString()} บาท/ฉบับ` },
          { k: "ค่า MFA", v: "200 บาท/ฉบับ (ปกติ) · 400 บาท (ด่วน)" },
          { k: "เริ่มต้น NYC", v: "3,000 บาท/ฉบับ (รวมแพ็ก)" },
          { k: "ที่ตั้งสถานทูต", v: c.embassyAddress },
          { k: "โทรศัพท์สถานทูต", v: c.embassyPhone },
        ]}
        heroBullets={[
          `แปลโดยนักแปลขึ้นทะเบียนสถานทูต${c.nameTh}`,
          `รับ-ส่งเอกสารถึงบ้าน/ออฟฟิศใน กทม.`,
          `ติดตามทุก milestone ผ่าน LINE`,
          `ตรวจเอกสารครบตามเกณฑ์หน่วยงานก่อนยื่น`,
          c.noteTh,
        ]}
        whoFor={{
          th: [
            `คู่สมรสไทย-${c.demonymEn} ที่ต้องใช้ Marriage Cert / Birth Cert ที่${c.nameTh}`,
            `ลูกจ้าง/วิศวกรไทยที่จะไปทำงานที่${c.nameTh} (Police + ปริญญา + Transcript)`,
            `บริษัทไทยที่ต้องตั้งสาขา / JV ที่${c.nameTh} (Board Resolution + หนังสือรับรองบริษัท)`,
          ],
          foreign: [
            `${c.demonymEn} citizens needing Thai-issued documents (marriage, birth, education) accepted in ${c.countryEn}`,
            `HR teams relocating Thai employees to ${c.countryEn}`,
            `Lawyers preparing cross-border Power of Attorney for ${c.countryEn} courts`,
          ],
        }}
        steps={[
          { name: "แปลและรับรองนักแปล", text: `แปลเป็น ${c.translationLang}` },
          { name: "Notary Public", text: "Notarial Services Attorney รับรอง ภายในวัน" },
          { name: "MFA แจ้งวัฒนะ", text: "กรมการกงสุล 2 วันทำการ" },
          { name: `สถานทูต${c.nameTh}`, text: c.embassyAddress },
          { name: "ส่งคืนถึงบ้าน", text: "Courier กทม. ฟรีเมื่อยอด > 5,000" },
        ]}
        pricing={[
          { label: "Notary + รับรองสำเนา", price: "1,500/ฉบับ", note: "Notarial Services Attorney" },
          { label: "แปล Certified", price: "800/หน้า", note: c.translationLang },
          { label: "MFA แจ้งวัฒนะ (ปกติ 2 วัน)", price: "200/ฉบับ", note: "ค่าธรรมเนียมราชการ" },
          { label: "MFA ด่วน 1 วัน", price: "400/ฉบับ", note: "Express counter" },
          { label: `สถานทูต${c.nameTh}`, price: `${c.embassyFeeTHB.toLocaleString()}/ฉบับ`, note: "ตามอัตราสถานทูต" },
          { label: "แพ็กเหมา NYC", price: "เริ่ม 3,000", note: `รวมทุกขั้น + courier + จบ ${c.totalDays} วัน` },
        ]}
        faqs={[
          { q: `เอกสารใดบ้างที่ส่ง${c.nameTh}ได้?`, a: `เอกสารยอดนิยม: ${c.topDocs.join(", ")} — NYC รับเอกสารอื่นได้ตามคำขอ` },
          { q: `ต้องแปลเป็นภาษาอะไร?`, a: c.translationLang },
          { q: `ใช้เวลาเท่าไหร่?`, a: `${c.totalDays} วันทำการ end-to-end ถ้าเอกสารพร้อม — ด่วนพิเศษติดต่อ LINE @nyc-notary` },
          { q: `ราคารวมทั้งหมดเท่าไหร่?`, a: `เริ่ม 3,000 บาท/ฉบับ (รวม MFA + courier) + ค่าสถานทูต${c.nameTh} ${c.embassyFeeTHB.toLocaleString()} บาท + แปลตามจำนวนหน้า (เริ่ม 800/หน้า)` },
          { q: `${c.nameTh}อยู่ใน Hague Apostille หรือไม่?`, a: c.noteTh },
          { q: `สถานทูต${c.nameTh}อยู่ที่ไหน?`, a: `${c.embassyAddress} โทร ${c.embassyPhone}` },
        ]}
        relatedLinks={[
          { to: "/mfa-legalisation", label: "← กลับหน้ารวม MFA Legalisation" },
          { to: "/apostille", label: "Apostille (122 ประเทศ Hague)" },
          { to: "/translate", label: "Certified Translation" },
          { to: "/notary", label: "Notary Public" },
          { to: "/translate/mfa", label: "MFA Translation Hub" },
        ]}
      />

      {nearby.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold sm:text-3xl">ประเทศใกล้เคียงใน{c.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link key={n.slug} to="/mfa-legalisation/$country" params={{ country: n.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-primary"
                  data-track-zone="mfa-detail-nearby-th">
                  <div className="font-bold">{n.flag} {n.nameTh}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{n.totalDays} วัน · {n.embassyFeeTHB.toLocaleString()} บาท</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <CountryDeepContent lang="th" service="mfa" slug={c.slug} countryTh={c.nameTh} countryEn={c.countryEn} flag={c.flag} hague={false} priceFromTh={c.embassyFeeTHB + 2500} turnaround={`${c.totalDays} วันทำการ`} />
    </>
  );
}
